import React from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Bot,
  Building2,
  CheckCircle2,
  Cloud,
  CloudCog,
  Cpu,
  HeartPulse,
  Rocket,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Palette,
  UsersRound,
  Workflow,
  
  MonitorCog,
} from 'lucide-react';

import './AboutUs.css';

const strengths = [
  {
    title: 'Security-First Architecture',
    text: 'Enterprise-grade security embedded in every solution we deliver.',
    icon: ShieldCheck,
  },
  {
    title: 'Scalable Platforms',
    text: 'Built to grow with your business using flexible, future-ready architecture.',
    icon: BarChart3,
  },
  {
    title: 'AI-Driven Innovation',
    text: 'Intelligent automation and AI capabilities that unlock efficiency and growth.',
    icon: Bot,
  },
  {
    title: 'Cloud Expertise',
    text: 'Modern cloud platforms that ensure agility, reliability, and performance.',
    icon: CloudCog,
  },
  {
    title: 'Operational Efficiency',
    text: 'Streamlined processes that reduce costs and improve productivity.',
    icon: Workflow,
  },
  {
    title: 'Mobile Application',
    text: 'Responsive mobile apps built for smooth performance, security, and user adoption.',
    icon: Smartphone,
  },
  {
    title: 'UI/UX Design',
    text: 'Clean, intuitive digital experiences designed around real user workflows.',
    icon: Palette,
  },
];

const expertise = [
  {
    title: 'AI & Automation',
    text: 'Intelligent automation, machine learning, and AI-powered solutions that drive transformation.',
    icon: Cpu,
  },
  {
    title: 'Cybersecurity',
    text: 'Protecting businesses with next-gen security frameworks, threat detection, and risk management.',
    icon: ShieldCheck,
  },
  {
    title: 'Payroll & HR Platforms',
    text: 'End-to-end payroll processing, HRMS, compliance, and workforce management solutions.',
    icon: UsersRound,
  },
  {
    title: 'Cloud Engineering',
    text: 'Cloud migration, DevOps, and infrastructure engineering for modern enterprise workloads.',
    icon: Cloud,
  },
  {
    title: 'Enterprise Technology Platforms',
    text: 'Custom enterprise applications and digital solutions tailored to your business needs.',
    icon: MonitorCog,
  },
];

const industries = [
  { title: 'Healthcare', icon: HeartPulse },
  { title: 'SaaS', icon: Cloud },
  { title: 'E-commerce', icon: ShoppingCart },
  { title: 'Startups', icon: Rocket },
  { title: 'Enterprise Operations', icon: Building2 },
];

const heroStatements = [
  'Innovation With Courage',
  'Reframe The Future',
  'Building Tomorrow Today',
  'The Future, Reimagined',
];

const IconBadge = ({ icon: Icon }) => (
  <span className="about-icon-badge" aria-hidden="true">
    <Icon size={28} strokeWidth={2.2} />
  </span>
);

const AboutUs = () => {
  return (
    <main className="about-page-wrapper">
      <section className="about-hero-section">
        <video
          className="about-hero-video"
          src="https://blackroth-images.s3.ap-south-1.amazonaws.com/about.mp4"
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
        <div className="about-hero-grid">
          <div className="about-hero-copy">
            <h1 className="about-hero-rotator" aria-label={heroStatements.join('. ')}>
              {heroStatements.map((statement, index) => (
                <span
                  className="about-hero-phrase"
                  key={statement}
                  style={{ '--phrase-index': index }}
                  aria-hidden="true"
                >
                  {statement.split(' ').map((word, wordIndex) => (
                    <span
                      className="about-hero-word"
                      key={`${statement}-${word}-${wordIndex}`}
                      style={{
                        '--word-index': wordIndex,
                        '--word-direction': wordIndex % 2 === 0 ? -1 : 1,
                      }}
                    >
                      {word}
                    </span>
                  ))}
                </span>
              ))}
            </h1>
          </div>
        </div>
      </section>

      <section className="about-section about-intro-section">
        <div className="about-two-column">
          <div className="about-section-copy">
            <p className="about-kicker">Who We Are</p>
            <h2>We Build Secure, Scalable & Intelligent Digital Ecosystems</h2>
            <p>
              BlackRoth is an enterprise technology partner helping businesses
              modernize, automate, and accelerate operations with confidence.
            </p>
            <p>
              Our solutions combine deep domain expertise, advanced AI capabilities,
              robust cybersecurity, and cloud-native engineering to deliver
              measurable business impact.
            </p>
          </div>

          <div className="about-br-visual" aria-label="BlackRoth secure digital ecosystem visual">
            <div className="about-br-city" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>
            <div className="about-br-stage">
              <div className="about-br-platform" aria-hidden="true" />
              <div className="about-br-shield" aria-hidden="true">BR</div>
              <div className="about-br-node about-br-node-ai">
                <Bot size={28} aria-hidden="true" />
                <span>AI-Driven Innovation</span>
              </div>
              <div className="about-br-node about-br-node-cloud">
                <Cloud size={28} aria-hidden="true" />
                <span>Cloud-Native Engineering</span>
              </div>
              <div className="about-br-node about-br-node-security">
                <ShieldCheck size={28} aria-hidden="true" />
                <span>Cybersecurity Excellence</span>
              </div>
              <div className="about-br-node about-br-node-enterprise">
                <UsersRound size={28} aria-hidden="true" />
                <span>Enterprise Platforms</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section about-soft-section">
        <div className="about-centered-heading">
          <p className="about-kicker">Why Businesses Choose BlackRoth</p>
          <h2>Built for Security. Designed for Scale. Driven by Impact.</h2>
        </div>

        <div className="about-strength-grid">
          {strengths.map((item) => (
            <article className="about-feature-card" key={item.title}>
              <IconBadge icon={item.icon} />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="about-section about-expertise-section">
        <div className="about-centered-heading">
          <p className="about-kicker">Our Expertise</p>
          <h2>End-to-End Enterprise Technology Platforms</h2>
        </div>

        <div className="about-expertise-grid">
          {expertise.map((item) => (
            <article className="about-service-card" key={item.title}>
              <IconBadge icon={item.icon} />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>

        <div className="about-industries">
          <div className="about-centered-heading">
            <p className="about-kicker">Industries We Support</p>
            <h2>Empowering Businesses Across Every Sector</h2>
          </div>

          <div className="about-industry-grid">
            {industries.map((industry) => {
              const Icon = industry.icon;

              return (
                <div className="about-industry-card" key={industry.title}>
                  <Icon size={34} strokeWidth={2} aria-hidden="true" />
                  <span>{industry.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="about-section about-vision-section">
        <div className="about-city-visual">
          <video
            className="about-vision-video"
            src="https://blackroth-images.s3.ap-south-1.amazonaws.com/graph.mp4"
            autoPlay
            muted
            loop
            playsInline
            aria-label="Animated business growth graph"
          />
        </div>

        <div className="about-section-copy">
          <p className="about-kicker">Our Vision for the Future</p>
          <h2>Building a Smarter, Safer & More Connected Tomorrow</h2>
          <p>
            Our vision is to be a global leader in intelligent enterprise solutions
            by combining innovation, security, and technology excellence.
          </p>
          <p>
            We aim to help businesses navigate digital transformation with confidence
            and build resilient, future-ready organizations.
          </p>
          <ul className="about-check-list">
            <li>
              <CheckCircle2 size={18} aria-hidden="true" />
              Innovation-led growth
            </li>
            <li>
              <CheckCircle2 size={18} aria-hidden="true" />
              Sustainable & scalable solutions
            </li>
            <li>
              <CheckCircle2 size={18} aria-hidden="true" />
              Long-term partnership approach
            </li>
          </ul>
        </div>
      </section>

      <section className="about-cta-section">
        <p className="about-kicker">Let's Build the Future Together</p>
        <h2>Ready to Transform Your Business?</h2>
        <p>
          Partner with BlackRoth and unlock the power of secure, intelligent,
          and scalable enterprise technology.
        </p>
        <div className="about-hero-actions">
          <Link className="about-primary-link" to="/contact-us">
            Contact Us Today
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutUs;
